import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '@opus/types';

/**
 * THE api client (brief 003-000-001 §3.1).
 * Base is relative — same-origin via the preview proxy. 401s trigger ONE
 * shared refresh (single-flight) and replay the failed request exactly once.
 */

export interface ApiErrorShape {
  status: number;
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

type RefreshFn = () => Promise<string | null>;

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export function createApiClient(
  getAccessToken: () => string | null | undefined,
  refresh: RefreshFn,
): AxiosInstance {
  const client = axios.create({ baseURL: '/api/v1', timeout: 30_000, headers: { 'Content-Type': 'application/json' } });
  // ONE single-flight wrapper per client — creating it per-call would dedupe nothing
  const refreshOnce = singleFlight(refresh);

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    async (err: AxiosError) => {
      const original = err.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
      const status = err.response?.status;
      const onAuthPath = original?.url?.startsWith('/auth/');

      if (status === 401 && original && !original._retried && !onAuthPath) {
        original._retried = true;
        const newToken = await refreshOnce();
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return client.request(original);
        }
        onUnauthorized?.();
      }

      throw normalizeError(err);
    },
  );

  return client;
}

/** Map axios failures onto the typed ApiError (envelope-aware). */
export function normalizeError(err: AxiosError): ApiError {
  const status = err.response?.status ?? 0;
  const requestId = (err.response?.headers?.['x-request-id'] as string | undefined) ?? undefined;
  const data = err.response?.data as { error?: { code?: string; message?: string; details?: unknown } } | undefined;
  return new ApiError(status, data?.error?.code ?? 'network_error', data?.error?.message ?? err.message, data?.error?.details, requestId);
}

/**
 * Single-flight wrapper (brief 003-000-001 §3.1): concurrent callers share one
 * in-flight promise; the next caller after completion starts a fresh one.
 * This is the audited client's refresh-queue deadlock, structurally removed.
 */
export function singleFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let inflight: Promise<T> | null = null;
  return () => {
    if (!inflight) {
      inflight = fn().finally(() => {
        inflight = null;
      });
    }
    return inflight;
  };
}
